import { ReactNode } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { AgentNameProvider } from '../../lib/use-agent-name';
import { TaskSidePanelProvider } from './task-side-panel/TaskSidePanelContext';
import { PersonalSaleSuppressionProvider } from '../../lib/personal-sale-suppression';
import { DocViewerHost } from './doc-viewer';
import { AppFrame } from './AppFrame';
import { PendingProfileThreadResume } from './profile-links/PendingProfileThreadResume';

// Symphony's root: brand <Head> (title / meta / favicon / og) + the persistent
// chrome. This is the Symphony half of the single brand fork in `_app.tsx`;
// nothing here branches on brand — the whole subtree is Symphony.
//
// Onboarding has its own self-contained shell (no sidebar, no brand header),
// so only the main app surfaces sit inside AppFrame; the chrome then persists
// across their route changes.
//
// The agent-OAuth consent hop is chrome-free for a stronger reason than looks: it renders in a POPUP
// the agent platform opened, it is a decision page and nothing else, and every piece of app chrome on
// it is either a dead end (a sidebar that navigates away mid-grant) or actively harmful — the agents
// island floats over the "Connect X to Symphony?" heading, so the one sentence the user must read to
// consent is the one covered up.
//
// `/welcome` is the signup loader (CreateRecipientOnEnter) and belongs here for the same reason the old
// `/onboarding/*` loader it replaced did: it runs for a caller who has NO tenant yet, so mounting the
// tenant-scoped chrome around it (AppFrame's duplexer, FloatingChat's chat channel, AgentNameProvider's
// personality read) fires a burst of calls that can only fail — duplexer-instance 409, personality 401,
// site-language 404 — against a metasite that does not exist. Pure noise and load on the first screen a
// new user ever sees. Keeping it chrome-free restores the behavior the loader had under `/onboarding`.
//
// `/s/<token>` — the public share card — is chrome-free for the strongest version of the same reason:
// its reader is NOT a Symphony user. There is no session, no metasite and no tenant, so every piece of
// chrome (the duplexer, the chat channel, the personality read) is a call that can only fail, and a
// sidebar full of an app they have no account for is the wrong first impression of the thing a person
// they know just sent them.
// `/pack-open` — the iOS pack-invite hand-off — is `/s/`'s case exactly: it is only ever reached when
// the OS declined the universal link, which means an in-app browser (WhatsApp, Gmail) with its own
// cookie jar, so the reader arrives session-less even when signed in elsewhere. Chrome around it is a
// sidebar of dead rows behind a burst of failing calls, on a page that exists to be left within a
// second. `lib/public-paths.js` and `lib/onboarding-gate.js` already let that reader through the auth
// gates; this is the third half of the same decision.
// `/me/<handle>` — a public profile link — is `/s/`'s case too: a stranger who scanned someone's QR, with
// no session and no tenant, on a page that is the owner's card and nothing else.
const CHROME_FREE_ROUTES = ['/onboarding', '/agent-oauth', '/welcome', '/s/', '/pack-open', '/me/'];

export function SymphonyApp({ children }: { children: ReactNode }) {
  const router = useRouter();
  const inAppFrame = !CHROME_FREE_ROUTES.some((route) => router.pathname.startsWith(route));
  return (
    <>
      <Head>
        <title>Symphony</title>
        {/* `interactive-widget=resizes-content` makes Chrome actually shrink the layout
            viewport (and 100dvh) for the on-screen keyboard instead of leaving that
            ambiguous — without it, Android Chrome's default behavior can scroll the
            document to bring a focused field into view while the layout viewport stays
            put, which can strand a `position: fixed` panel (the narrow chat sheet, its
            header included) off the top of the screen mid-resize. iOS Safari and desktop
            ignore the hint, and `useKeyboardInset` already treats "0" as the no-op case
            for browsers that resize the layout viewport on their own — this makes Android
            Chrome one of them instead of an unhandled third behavior. */}
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=resizes-content" />
        {/* The iOS Smart App Banner `apple-itunes-app` <meta> lives in `pages/_document.tsx`,
            hoisted to the TOP of <head> so Safari's parser reaches it before the blocking
            scripts — earliest possible banner. Keep it out of this next/head to avoid a
            duplicate tag. */}
        {/* Symphony voice from the brand essay: warm, present, "your team's ensemble". */}
        <meta
          name="description"
          content="Your team’s AI ensemble — a Maestro and a set of specialists working alongside you in one shared room."
        />
        {/* Keyed, so a page's own card (`/me/<handle>`'s profile OG) REPLACES these instead of adding a
            second og:title beside them — next/head dedupes `property` metas only by key. Keep the
            description unkeyed: a keyed `name` meta is no longer deduped by name. */}
        <meta key="og:type" property="og:type" content="website" />
        <meta key="og:site_name" property="og:site_name" content="Symphony" />
        <meta key="og:title" property="og:title" content="Symphony" />
        <meta
          key="og:description"
          property="og:description"
          content="Your team’s AI ensemble — a Maestro and a set of specialists working alongside you in one shared room."
        />
        {/* Unfurlers (WhatsApp/Slack/FB) require ABSOLUTE og URLs — a relative
            og:image is ignored and the share falls back to a bare domain card. */}
        <meta key="og:url" property="og:url" content="https://symphony.wix.com/" />
        {/* 1200×630 social card in /public — PNG so unfurlers render it. */}
        <meta key="og:image" property="og:image" content="https://symphony.wix.com/symphony-social.png" />
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta key="twitter:image" name="twitter:image" content="https://symphony.wix.com/symphony-social.png" />
        <meta key="twitter:title" name="twitter:title" content="Symphony" />
        <meta
          key="twitter:description"
          name="twitter:description"
          content="Your team’s AI ensemble — a Maestro and a set of specialists working alongside you in one shared room."
        />
        <link key="favicon-32" rel="icon" type="image/png" sizes="32x32" href="/32-favicon.png" />
        <link key="favicon-16" rel="icon" type="image/png" sizes="16x16" href="/16-favicon.png" />
        <link key="apple-touch-icon" rel="apple-touch-icon" sizes="180x180" href="/300-site-logo.png" />
      </Head>
      <AgentNameProvider>
        {inAppFrame ? (
          <TaskSidePanelProvider>
            {/* Above AppFrame, not inside it: the frame READS the claim to decide whether to draw the
                personal-sale band, and a component cannot read a context it provides itself. */}
            <PersonalSaleSuppressionProvider>
              <AppFrame>{children}</AppFrame>
            </PersonalSaleSuppressionProvider>
          </TaskSidePanelProvider>
        ) : (
          children
        )}
        {/* The document viewer is presented HERE — once, outside the framed/chrome-free branch — exactly
            as native hosts it once at the app root (`RootView.swift` / `AppRoot.kt`). `SymphonyApp` lives
            in `_app`, above the page component, so this node survives every route change and outlives any
            chat row, Files panel or side panel that opens a report; and being outside the branch it also
            covers the non-framed routes and the standalone `pages/workspace/[slug]/files/[file]` page.
            State lives in `lib/doc-viewer-presenter.ts`; call sites only call `openDocViewer(...)`. */}
        <DocViewerHost />
        {/* A profile link tapped before sign-up resumes once the visitor is in (design §6.1). */}
        <PendingProfileThreadResume />
      </AgentNameProvider>
    </>
  );
}
